<template>
    <div>
        <div v-if="styleList.length === 0">
            {{ $t('styling.noResult') }}
        </div>
        <template v-if="styleList">
            <v-draggable :list="styleList" handle=".fa-arrows-alt">
                <div
                    v-for="(style, index) in styleList"
                    :key="index"
                    class="accordion"
                    role="tablist"
                >
                    <b-card no-body class="mb-1" v-if="style.mode !== 'delete'">
                        <b-card-header
                            header-tag="header"
                            :class="headerClass(index)"
                            role="tab"
                            @click="$_selectStyle(index)"
                            data-toggle="collapse"
                        >
                            <em class="fa fa-arrows-alt"></em>
                            <span>{{ $_styleNameHeader(style) }}</span>
                            <div class="float-right">
                                <a
                                    :title="$t('styling.delete')"
                                    class="pr-4"
                                    @click.stop.prevent="$_onRemoveStyle(index)"
                                >
                                    <em class="fas fa-trash-alt fa-sm"></em>
                                </a>
                                <em class="fa iconCollapseExpand"></em>
                            </div>
                        </b-card-header>
                        <b-collapse
                            :id="`accordion-${index}`"
                            accordion="my-accordion"
                            v-model="accordionState[index]"
                            role="tabpanel"
                        >
                            <b-card-body v-if="accordionState[index] === true">
                                <!--Name-->
                                <x-input
                                    v-if="$_isEditable(style)"
                                    :id="'styles-layer-name'"
                                    :name="'styles-layer-name'"
                                    :text="$t('columnNameName')"
                                    v-model="style.stylename"
                                    :maxlength="50"
                                    @input="$_onStyleName(style)"
                                    :state="!style.errorName"
                                    :invalidMessage="
                                        style.invalidNameErrorMessage
                                    "
                                    required
                                >
                                </x-input>
                                <!--Title-->
                                <x-input
                                    v-if="!$_isImportStyle(style)"
                                    :id="'styles-layer-title'"
                                    :name="'styles-layer-title'"
                                    :text="$t('columnNameTitle')"
                                    v-model="style.styleTitle"
                                    :maxlength="50"
                                    @input="$_onStyleTitle(style)"
                                    :state="!style.errorTitle"
                                    :invalidMessage="
                                        style.invalidTitleErrorMessage
                                    "
                                    required
                                >
                                </x-input>
                                <!--Legend-->
                                <div
                                    v-if="style.legendImageData"
                                    :class="{ 'mb-3': !$_isImportStyle(style) }"
                                >
                                    <b-link
                                        v-if="!$_isImportStyle(style)"
                                        @click="
                                            style.legendClosed =
                                                !style.legendClosed
                                        "
                                    >
                                        {{
                                            style.legendClosed
                                                ? $t('styling.legendShow')
                                                : $t('styling.legendHide')
                                        }}
                                    </b-link>
                                    <label v-else>{{
                                        $t('labelLegend')
                                    }}</label>
                                    <div
                                        v-show="
                                            $_isImportStyle(style) ||
                                            !style.legendClosed
                                        "
                                    >
                                        <b-img
                                            :src="style.legendImageData"
                                            fluid
                                            :alt="
                                                $t('styling.resourcesNoResult')
                                            "
                                        ></b-img>
                                    </div>
                                </div>
                                <!--Style-->
                                <default-style-tab
                                    v-if="style.style"
                                    :selectedStyle="style.style"
                                    :inputFields="informationAttributes"
                                    :domains="domains"
                                    :geometryTypeArray="geometryTypeArray"
                                    :layerSourceName="layerSourceName"
                                >
                                </default-style-tab>
                            </b-card-body>
                        </b-collapse>
                    </b-card>
                </div>
            </v-draggable>
        </template>
        <div>
            <b-link :id="`addStyle`" :name="`addStyle`" @click="$_addStyle">
                <em class="fa fa-plus pr-1"></em>{{ $t('styling.add') }}
            </b-link>
        </div>
    </div>
</template>

<script>
    import { DEFAULT_FEATURE_STYLE_SETTINGS_NORMAL } from '../../../constants';
    import { capitalizeValue } from '../../../services/utilities';
    import { validateSqlName } from '../../../services/utilities/validator';
    import modalMixin from '../../../components/Mixins/ModalMixin.vue';
    import defaultStyleTab from '../../../components/dashboard/StylingOptions/DefaultStyleTab.vue';
    import XInput from '../../BaseComponents/x-input.vue';
    import draggable from 'vuedraggable';

    const styleSettingsTemplate = {
        editable: true,
        stylename: '',
        styleTitle: '',
        errorName: false,
        errorTitle: false,
        invalidNameErrorMessage: null,
        invalidTitleErrorMessage: null,
        style: { ...DEFAULT_FEATURE_STYLE_SETTINGS_NORMAL }
    };

    export default {
        mixins: [modalMixin],
        props: {
            layerName: { type: String, required: true },
            layerId: { type: Number, required: true },
            geometryTypeArray: {
                type: Array,
                required: false,
                default: () => []
            },
            informationAttributes: {
                type: Array,
                required: false,
                default: () => []
            },
            layerSourceName: {
                type: String,
                required: false,
                default: ''
            }
        },
        components: {
            'default-style-tab': defaultStyleTab,
            'x-input': XInput,
            'v-draggable': draggable
        },
        data() {
            return {
                styleList: [],
                accordionState: [],
                domains: [],
                newStyleId: -1 // Startpoint for new styles. They get a negative ID value
            };
        },
        created() {
            this.init();
        },
        watch: {},
        computed: {
            headerClass() {
                return (index) => {
                    return {
                        'p-1': true,
                        collapsed: !this.accordionState[index],
                        'not-collapsed': this.accordionState[index]
                    };
                };
            },
            $_isEditable() {
                return (style) => {
                    return style.editable;
                };
            },
            $_isImportStyle() {
                return (style) => {
                    return style.style === null;
                };
            },
            $_styleNameGeoServer() {
                return (stylename) => {
                    return `${this.layerName}_${stylename}`;
                };
            },
            $_styleNameHeader() {
                return (style) => {
                    return this.$_isEditable(style) && style.stylename
                        ? this.$_styleNameGeoServer(style.stylename).toUpperCase()
                        : style.stylename.toUpperCase();
                };
            }
        },
        methods: {
            async init() {
                await this.loadStyles();
                await this.$_loadDomains();
            },
            async loadStyles() {
                try {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'GetLayerStyles',
                        parameters: { Layername: this.layerName }
                    });
                    if (!result.data) {
                        return;
                    }
                    this.styleList = [...result.data].map((style) => {
                        if (typeof style.settings === 'string') {
                            return { ...style };
                        }
                        style.editable = false;
                        style.mode = 'edit';
                        return style;
                    });
                    const layerStylePromisses = this.styleList.map((style) =>
                        this.$store.dispatch('useApiData', {
                            name: 'GetLayerStyle',
                            parameters: {
                                ThemeLayerId: this.layerId,
                                Stylename: style.stylename
                            }
                        })
                    );
                    const layerStyleResults = await Promise.all(
                        layerStylePromisses
                    );
                    this.styleList = this.styleList.map((style, index) => {
                        return {
                            id: index,
                            ...style,
                            styleTitle: layerStyleResults[index]?.data?.styleTitle,
                            style: layerStyleResults[index]?.data?.style,
                            legendClosed: true
                        };
                    });
                    this.$_collapseAllStyles();
                } catch (error) {
                    console.log(this.$t('styling.noResult'));
                }
            },
            validateStyles() {
                // Check if styles contains error or name is empty
                // or in case of customStylingOptions: check if attribute is empty
                const hasMissingAttribute = (style) => {
                    return style.style.customStylingOptions !== null &&
                        style.style.customStylingOptions.attribute === null;
                };
                return (
                    this.styleList.filter(
                        (style) =>
                            style.mode !== 'delete' &&
                            !this.$_isImportStyle(style) &&
                            (style.errorName ||
                                style.stylename.trim() === '' ||
                                style.errorTitle ||
                                style.styleTitle.trim() === '' ||
                                hasMissingAttribute(style))
                    ).length === 0
                );
            },
            async removeStyles() {
                const removePromisses = this.styleList.map((style) =>
                    this.$store.dispatch('useApiData', {
                        name: 'DeleteLayerStyle',
                        parameters: { Stylename: style.stylename }
                    })
                );
                return Promise.all(removePromisses);
            },
            async saveStyles() {
                this.$_collapseAllStyles();
                const styles = this.styleList
                    // do not save new styles that have been marked to be removed
                    // New (unsaved) styles have a negative ID value
                    .filter((style) => !(style.id < 0 && style.mode === 'delete'))
                    // do not save unchanged styles
                    .filter((style) => !(style.id > 0 && !style.mode));

                const layerStyles = styles
                    .filter((style) => style.mode === 'edit' || style.id < 0)
                    .map((style) => {
                        return {
                            ...style,
                            stylename: style.id < 0? this.$_styleNameGeoServer(style.stylename) : style.stylename
                        };
                    });

                if (layerStyles.length > 0) {
                    await this.$store.dispatch('useApiData', {
                        name: 'UpdateLayerStyle',
                        data: {
                            themeLayerId: this.layerId,
                            layerName: this.layerName,
                            layerStyles: [...layerStyles]
                        }
                    });
                }

                const deletedStyles = styles.filter(
                    (style) => style.mode === 'delete'
                );
                return Promise.all(
                    deletedStyles.map((style) => {
                        return this.$store.dispatch('useApiData', {
                            name: 'DeleteLayerStyle',
                            parameters: {
                                ThemeLayerId: this.layerId,
                                Stylename: style.stylename
                            }
                        });
                    })
                );
            },
            $_addStyle() {
                this.$_collapseAllStyles();
                // A new style has no ID value
                // Give it a negative value
                this.styleList.push({
                    ...styleSettingsTemplate,
                    style: { ...DEFAULT_FEATURE_STYLE_SETTINGS_NORMAL },
                    id: this.newStyleId--,
                    layerName: this.layerName
                });
                this.accordionState.push(true);
            },
            $_selectStyle(index) {
                const newVal = !this.accordionState[index];
                this.accordionState = this.styleList.map((format) => false);
                this.accordionState[index] = newVal;
            },
            async $_onRemoveStyle(deleteindex) {
                if (await this.$_styleExistsInTheme(deleteindex)) {
                    return;
                }
                const data = { message: this.$t('styling.deleteConfirmation') };
                this.msgBoxConfirm(data).then((confirmed) => {
                    if (confirmed) {
                        this.styleList = this.styleList.map((style, index) => ({
                            ...style,
                            mode: index === deleteindex ? 'delete' : style.mode
                        }));
                    }
                });
            },
            async $_onStyleName(style) {
                // auto fill title
                style.styleTitle = capitalizeValue(style.stylename);
                this.$_onStyleTitle(style);

                // validation
                style.errorName = false;
                if (!validateSqlName(style.stylename)) {
                    style.errorName = true;
                    style.invalidNameErrorMessage = this.$t(
                        'errorInvalidColumnName'
                    );
                    return;
                }
                const resultExists = await this.$_styleNameExists(style.stylename);
                if (resultExists.error) {
                    style.errorName = true;
                    style.invalidNameErrorMessage = resultExists.inUseGeoServer
                        ? this.$t('styling.nameInUseGeoServer')
                        : this.$t('styling.nameInUse');
                }
            },
            $_onStyleTitle(style) {
                style.errorTitle = false;
                if (this.$_styleTitleExists(style.styleTitle)) {
                    style.errorTitle = true;
                    style.invalidTitleErrorMessage = this.$t('styling.titleInUse');
                }
            },
            async $_loadDomains() {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetFeedbackDomainOverview'
                });
                if (result?.data) {
                    this.domains = result.data;
                }
            },
            $_collapseAllStyles() {
                this.accordionState = this.styleList.map(() => false);
            },
            async $_styleExistsInTheme(deleteindex) {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetStyleIsInUse',
                    parameters: {
                        ThemeLayerId: this.layerId,
                        Stylename: this.styleList[deleteindex]?.stylename
                    }
                });
                if (result?.data) {
                    this.showToast({
                        message: this.$t('styling.inUseTheme'),
                        variant: 'danger',
                        autoHideDelay: 5000
                    });
                    return true;
                }
                return false;
            },
            $_styleTitleExists(styleTitle) {
                // already exists in form
                const styles = this.styleList.filter((style) => {
                    return (
                        style.styleTitle &&
                        style.styleTitle.toLowerCase() === styleTitle.toLowerCase()
                    );
                });
                // do not count own occurrence
                return styles.length > 1;
            },
            async $_styleNameExists(stylename) {
                // already exists in form
                const styles = this.styleList.filter((style) => {
                    return (
                        style.stylename.toLowerCase() === stylename.toLowerCase()
                    );
                });
                // do not count own occurrence
                if (styles.length > 1) {
                    return {
                        error: true,
                        inUseGeoServer: false
                    };
                }
                // already exists in GeoServer
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetStyleExist',
                    parameters: { Stylename: this.$_styleNameGeoServer(stylename) }
                });
                if (result?.data) {
                    return {
                        error: true,
                        inUseGeoServer: true
                    };
                }
                return { error: false };
            }
        }
    };
</script>

<style scoped lang="scss">
.fa-arrows-alt:hover {
    cursor: move;
}

.accordion > .card {
    overflow: initial;
}
</style>
